import AppSpinner from "../../../elements/AppSpinner";
import { Button, Col, Row } from "reactstrap";
import DisplayRecord from "../DisplayRecord";
import Text from "../../Inputs/Text";
import Constants from "../../../utils/Constants";
import { Popover, Whisper, WhisperInstance } from "rsuite";
import { forwardRef, Ref, useImperativeHandle, useRef } from "react";
import EmptyField from "../EmptyField";

interface Props {
  isEditing: boolean;
  setIsEditing: (val: any) => void;
  tableView: boolean;
  field: any;
  stopEditing: () => void;
  type: string;
  isDisabled: boolean;
  save: (callback: () => void) => void;
  existingValue: any;
  inputSize: string;
  handleChange: () => void;
  value: any;
  submitting: boolean;
  localError: any;
  error: any;
  mode: "r" | "rw";
  modifyTrigger: any;
  openModify: any;
}

const TextRecordDrawer = (
  {
    tableView,
    field,
    stopEditing,
    type,
    isDisabled,
    save,
    existingValue,
    inputSize,
    handleChange,
    value,
    submitting,
    localError,
    error,
    mode,
    modifyTrigger,
    openModify,
  }: Props,
  ref?: Ref<any>
) => {
  let errorValue = false;
  const whisperRef = useRef<WhisperInstance>(null);
  const handleOpenModify = () => {
    openModify();
    whisperRef?.current?.open();
  };

  const handleCloseModify = () => {
    stopEditing();
    whisperRef.current?.close();
  };

  useImperativeHandle(ref, () => ({
    focus: () => {
      whisperRef?.current?.open();
    },
  }));

  // Check for erroneous values.
  if (existingValue) {
    const isString = typeof existingValue === "string";
    const isNumber = typeof existingValue === "number";

    if (!isString && !isNumber) {
      mode = "r";
      errorValue = true;
    }
  }

  return (
    <Whisper
      preventOverflow
      ref={whisperRef}
      placement="autoVerticalStart"
      trigger={mode === "r" ? "none" : "click"}
      onClose={handleCloseModify}
      speaker={
        <Popover
          style={{
            boxShadow: "0 1px 8px rgba(0,0,0,0.12)",
            filter: "drop-shadow(var(--rs-popover-shadow))",
          }}
          className="p-0"
          arrow={false}
        >
          <div>
            <div
              className="cursor-pointer p-2 text-dark border-bottom border-superlight"
              style={{ width: 350 }}
            >
              <Row className="align-items-center">
                <Col xs="">
                  <h5 className="text-dark m-0">{field?.Name}</h5>
                </Col>
                <Col xs="auto">
                  <Button
                    size="sm"
                    outline
                    color="secondary"
                    onClick={(e) => {
                      e.preventDefault();

                      handleCloseModify();
                    }}
                  >
                    Done
                  </Button>
                </Col>
              </Row>
            </div>
            <div className="p-2">
              <Row>
                <Col
                  className={`${type !== "LongText" && "pr-2"}`}
                  xs={type === "LongText" ? 12 : ""}
                >
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (isDisabled) return;
                      save(() => whisperRef.current?.close());
                    }}
                  >
                    <Text
                      type={type}
                      existingValue={existingValue}
                      field={field}
                      inputSize={inputSize}
                      onChange={handleChange}
                      autoFocus
                      disabled={isDisabled}
                    />
                  </form>
                </Col>
                <Col
                  className={`${type !== "LongText" && "pl-0"} ${
                    type === "LongText" && "mt-2 text-right"
                  }`}
                  xs={type === "LongText" ? 12 : "auto"}
                >
                  <Button
                    disabled={(field?.Required && !value) || submitting}
                    onClick={(e) => {
                      e.preventDefault();
                      save(() => whisperRef.current?.close());
                    }}
                    size="sm"
                    color="primary"
                    className="btn-icon-only"
                  >
                    {submitting ? (
                      <AppSpinner size="sm" color="white" />
                    ) : (
                      <i className="mdi mdi-check" style={{ fontSize: 20 }} />
                    )}
                  </Button>
                </Col>
              </Row>
            </div>

            {(localError || error) && (
              <div className="mb-0 text-left p-2 border-top border-superlight">
                <p className="m-0 text-danger">
                  {error && (
                    <small>Unable to save your changes. Try again.</small>
                  )}
                  {localError && <small>{localError}</small>}
                </p>
              </div>
            )}
          </div>
        </Popover>
      }
    >
      <div>
        <DisplayRecord
          mode={mode}
          //@ts-ignore
          readonly={(mode === "r" || field?.ReadOnly) ?? false}
          tableView={tableView}
          modifyTrigger={modifyTrigger}
          openModify={handleOpenModify}
          isSensitive={field?.Sensitive ?? false}
          displayComponent={(_: any, innerRef: any) => {
            if (errorValue) {
              return (
                <TextError
                  val={existingValue}
                  innerRef={innerRef}
                  type={field.FieldType}
                />
              );
            }

            return (
              <div className="tw:max-w-92 tw:min-w-5.5 tw:truncate">
                {field.FieldType !== Constants.FIELDTYPES.URL &&
                  field.FieldType !== Constants.FIELDTYPES.PERCENT &&
                  field && (
                    <>
                      {(existingValue || existingValue === 0) && field ? (
                        existingValue
                      ) : (
                        <EmptyField table={tableView} />
                      )}
                    </>
                  )}
                {field.FieldType === Constants.FIELDTYPES.PERCENT && field && (
                  <>
                    {existingValue !== null &&
                    existingValue !== undefined &&
                    field ? (
                      existingValue + "%"
                    ) : (
                      <EmptyField table={tableView} />
                    )}
                  </>
                )}
                {field.FieldType === Constants.FIELDTYPES.URL &&
                  field &&
                  existingValue && (
                    <a href={existingValue} target="_blank" rel="noreferrer">
                      {existingValue.toLowerCase()},
                    </a>
                  )}
                {!existingValue &&
                  field &&
                  field.FieldType === Constants.FIELDTYPES.URL && (
                    <EmptyField table={tableView} />
                  )}
              </div>
            );
          }}
        />
      </div>
    </Whisper>
  );
};

export default forwardRef(TextRecordDrawer);

function TextError({
  val,
  type,
  innerRef,
}: {
  val: any;
  type: string;
  innerRef: any;
}) {
  let value;

  try {
    value = JSON.stringify(val, null, 2);
  } catch {
    value = "Invalid JSON";
  }

  return (
    <div
      ref={innerRef}
      style={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "-webkit-box",
        WebkitLineClamp: 3,
        WebkitBoxOrient: "vertical",
      }}
    >
      <div className="tw:text-danger tw:font-medium">
        <i className="mdi mdi-alert-circle-outline tw:text-danger tw:pr-1" />
        Invalid Value For {type} Field:
      </div>
      <pre className="tw:p-1 tw:rounded tw:bg-superlight tw:border tw:border-gray-200">
        {value}
      </pre>
    </div>
  );
}
